import{useCallback,useEffect,useMemo,useState}from"react";
import{WorkspaceHeader}from"../../components/Foundation";
import{EmptyState,LoadingLine}from"../../components/State";
import{
  identityAccessApi,
  type ControlPlane,
  type PendingRegistration,
}from"../../lib/identityAccessApi";
import{unlockSecurityAuthority}from"../../lib/securityAuthority";

type Draft={
  login_profile_key:string;
  role_key:string;
  package_key:string;
  reason:string;
};

function err(e:unknown){
  return e instanceof Error?e.message:String(e);
}

export function RegistrationsView(){
  const[rows,setRows]=useState<PendingRegistration[]>([]);
  const[control,setControl]=useState<ControlPlane|null>(null);
  const[drafts,setDrafts]=useState<Record<string,Draft>>({});
  const[loading,setLoading]=useState(true);
  const[error,setError]=useState<string|null>(null);
  const[unlockBusy,setUnlockBusy]=useState(false);
  const[busy,setBusy]=useState<string|null>(null);
  const[info,setInfo]=useState<string|null>(null);

  const load=useCallback(async()=>{
    setLoading(true);
    setError(null);
    try{
      const[pending,cp]=await Promise.all([
        identityAccessApi.pendingRegistrations(),
        identityAccessApi.controlPlane(),
      ]);
      const nextRows=pending.registrations??[];
      const nextControl=cp.control_plane;
      setRows(nextRows);
      setControl(nextControl);

      const profiles=(nextControl.login_profiles??[])
        .filter(p=>p.profile_key!=="PUBLIC_REGISTRATION");
      const preferred=
        profiles.find(p=>p.profile_key==="STANDARD_MFA")?.profile_key||
        profiles[0]?.profile_key||
        "";

      setDrafts(old=>{
        const next={...old};
        for(const row of nextRows){
          if(!next[row.user_id]){
            next[row.user_id]={
              login_profile_key:row.login_profile_key||preferred,
              role_key:"",
              package_key:"",
              reason:"Owner approved verified registration",
            };
          }
        }
        return next;
      });
    }catch(e){
      setError(err(e));
    }finally{
      setLoading(false);
    }
  },[]);

  useEffect(()=>{void load()},[load]);

  const profiles=useMemo(
    ()=>(control?.login_profiles??[])
      .filter(p=>p.profile_key!=="PUBLIC_REGISTRATION"),
    [control],
  );
  const roles=control?.roles??[];
  const packages=control?.packages??[];

  const authorityRequired=
    /authority_step_up_required|permission_denied|access_management_denied/i
      .test(error??"");
  const sessionRequired=
    /authentication_required|passkey_session_required/i
      .test(error??"");

  function change(id:string,patch:Partial<Draft>){
    setDrafts(old=>({
      ...old,
      [id]:{
        ...(old[id]??{
          login_profile_key:"",
          role_key:"",
          package_key:"",
          reason:"Owner approved verified registration",
        }),
        ...patch,
      },
    }));
  }

  async function unlock(){
    setUnlockBusy(true);
    setInfo(null);
    try{
      await unlockSecurityAuthority();
      await load();
    }catch(e){
      setInfo(err(e));
    }finally{
      setUnlockBusy(false);
    }
  }

  async function approve(row:PendingRegistration){
    const d=drafts[row.user_id];
    if(!row.email_verified||row.state!=="OWNER_PENDING"){
      setInfo("Only verified OWNER_PENDING registrations can be approved.");
      return;
    }
    if(!d?.login_profile_key){
      setInfo("Select a login profile.");
      return;
    }
    if(!d.reason.trim()){
      setInfo("Approval reason is required.");
      return;
    }

    setBusy(row.user_id);
    setInfo("Waiting for fresh passkey approval…");
    try{
      await identityAccessApi.approveRegistration(
        row.user_id,
        d.login_profile_key,
        d.role_key||null,
        d.package_key||null,
        d.reason.trim(),
        {
          onPasskey:()=>setInfo("Waiting for fresh passkey approval…"),
          onApplying:()=>setInfo("Applying canonical IAM assignment…"),
        },
      );
      setInfo("Registration approved.");
      await load();
    }catch(e){
      setInfo(err(e));
    }finally{
      setBusy(null);
    }
  }

  return <div className="page">
    <WorkspaceHeader
      eyebrow="Security Administration"
      title="Registrations"
      description="Verified identities awaiting Owner approval. Access is assigned only through canonical IAM."
      actions={loading?<LoadingLine/>:<button type="button" className="button secondary" onClick={()=>void load()}>Refresh</button>}
    />

    {(authorityRequired||sessionRequired)&&
      <div className="card" style={{marginBottom:"16px"}}>
        <strong>{sessionRequired?"Passkey session required":"Administrative authority required"}</strong>
        <p className="muted">
          {sessionRequired
            ?"Sign in with an approved passkey before viewing pending registrations."
            :"Unlock Security Authority with a fresh passkey to view pending registrations."}
        </p>
        {!sessionRequired&&
          <button type="button" className="button secondary" disabled={unlockBusy} onClick={()=>void unlock()}>
            {unlockBusy?"Waiting for passkey…":"Unlock with passkey"}
          </button>}
      </div>}

    {info&&<div className="card" style={{marginBottom:"16px"}}>{info}</div>}

    {!error&&!loading&&rows.length===0&&
      <EmptyState
        title="No pending registrations"
        detail="There are no registrations awaiting email verification or Owner approval."
      />}

    {!error&&rows.map(row=>{
      const d=drafts[row.user_id]??{
        login_profile_key:"",
        role_key:"",
        package_key:"",
        reason:"Owner approved verified registration",
      };
      const approvable=row.email_verified&&row.state==="OWNER_PENDING";

      return <section key={row.user_id} className="card" style={{marginBottom:"16px",display:"grid",gap:"12px"}}>
        <div>
          <strong>{row.display_name}</strong>
          <div className="muted">{row.email}</div>
        </div>

        <div style={{display:"flex",gap:"18px",flexWrap:"wrap"}}>
          <span><b>Provider:</b> {row.provider_key}</span>
          <span><b>Email:</b> {row.email_verified?"VERIFIED":"PENDING"}</span>
          <span><b>State:</b> {row.state}</span>
        </div>

        {!row.email_verified&&<div className="muted">Waiting for email verification.</div>}

        {approvable&&<>
          <label>
            <span>Login profile</span>
            <select value={d.login_profile_key} onChange={e=>change(row.user_id,{login_profile_key:e.target.value})}>
              <option value="">Select a login profile…</option>
              {profiles.map(p=><option key={p.profile_key} value={p.profile_key}>{p.profile_key}</option>)}
            </select>
          </label>

          <label>
            <span>Role</span>
            <select value={d.role_key} onChange={e=>change(row.user_id,{role_key:e.target.value})}>
              <option value="">— none —</option>
              {roles.map(r=><option key={r.role_key} value={r.role_key}>{r.role_key}</option>)}
            </select>
          </label>

          <label>
            <span>Access package</span>
            <select value={d.package_key} onChange={e=>change(row.user_id,{package_key:e.target.value})}>
              <option value="">— none —</option>
              {packages.map(p=><option key={p.package_key} value={p.package_key}>{p.package_key}</option>)}
            </select>
          </label>

          <label>
            <span>Approval reason</span>
            <input type="text" value={d.reason} onChange={e=>change(row.user_id,{reason:e.target.value})}/>
          </label>

          <div>
            <button
              type="button"
              className="button primary"
              disabled={busy===row.user_id||!d.login_profile_key||!d.reason.trim()}
              onClick={()=>void approve(row)}
            >
              {busy===row.user_id?"Approving…":"Approve registration"}
            </button>
          </div>
        </>}
      </section>;
    })}
  </div>;
}
